import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const INITIAL_CREW = [
  { id: 1, name: 'Kwame Osei', rank: 'Master/Captain', department: 'Deck', status: 'Available', bosiet: '2024-12-01', medical: '2024-05-15' },
  { id: 2, name: 'John Doe', rank: 'Chief Engineer', department: 'Engine', status: 'Deployed', bosiet: '2023-10-15', medical: '2024-02-28' },
];

const MarineCrew = () => {
  const [crew, setCrew] = useState(INITIAL_CREW);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', rank: 'Deckhand', department: 'Deck' });

  const handleAddCrew = (e) => {
    e.preventDefault();
    const newMember = {
      id: Date.now(),
      name: formData.name,
      rank: formData.rank,
      department: formData.department,
      status: 'Available',
      bosiet: 'Valid',
      medical: 'Valid'
    };
    setCrew([newMember, ...crew]);
    setShowModal(false);
    setFormData({ name: '', rank: 'Deckhand', department: 'Deck' });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Marine Crew Pool</h1>
          <p className="text-gray-500 text-sm">Validations and seafarer certifications tracker.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-[#11469A] text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-sm hover:bg-[#0d3678] flex items-center gap-2"
        >
          <SafeIcon name="UserPlus" size={18} /> Add Crew Member
        </button>
      </div>

      <div className="card border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 text-gray-400 font-bold uppercase text-[10px] tracking-widest border-b border-gray-100">
              <tr>
                <th className="px-6 py-4">Crew Member</th>
                <th className="px-6 py-4">Department</th>
                <th className="px-6 py-4">BOSIET</th>
                <th className="px-6 py-4">Medical</th>
                <th className="px-6 py-4 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {crew.map(m => (
                <tr key={m.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-bold text-gray-800">{m.name}</div>
                    <div className="text-[10px] text-[#11469A] font-bold uppercase">{m.rank}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{m.department}</td>
                  <td className="px-6 py-4"><span className="text-green-600 font-bold text-xs flex items-center gap-1"><SafeIcon name="CheckCircle" size={14}/> {m.bosiet}</span></td>
                  <td className="px-6 py-4"><span className="text-green-600 font-bold text-xs flex items-center gap-1"><SafeIcon name="CheckCircle" size={14}/> {m.medical}</span></td>
                  <td className="px-6 py-4 text-right">
                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                      m.status === 'Available' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
                    }`}>{m.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold text-[#11469A]">Register Seafarer</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><SafeIcon name="X" size={20} /></button>
              </div>
              <form onSubmit={handleAddCrew} className="p-6 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Full Name</label>
                  <input required type="text" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Rank</label>
                    <input required type="text" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.rank} onChange={e => setFormData({...formData, rank: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Department</label>
                    <select className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})}>
                      <option>Deck</option>
                      <option>Engine</option>
                      <option>Catering</option>
                    </select>
                  </div>
                </div>
                <div className="pt-4 flex gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex-1 py-3 bg-[#11469A] text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-[#0d3678]">Add to Pool</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MarineCrew;